/**
 * Map Styling Utilities
 *
 * Shared utilities for map styling, color calculations, and feature styling.
 */

import type { PathOptions } from "leaflet";
import type { ColorBin } from "@/components/maps/insets";

/**
 * Get color based on value and bins.
 * Uses exclusive upper bound (value < bin.max) so boundary values fall into
 * the higher bin — critical for binary threat flags where 0 = No Threat
 * (bin.max = 1) and 1 = Threat (bin.min = 1), ensuring value 1.0 correctly
 * matches the Threat bin and not the No Threat bin.
 * Falls back to the last bin color if no bin matches.
 */
export function getColorForValue(value: number, bins: ColorBin[]): string {
  for (const bin of bins) {
    if (value >= bin.min && value < bin.max) {
      return bin.color;
    }
  }
  // Fallback: last bin catches the upper boundary (e.g., max = 9999999999)
  return bins[bins.length - 1]?.color || "#1976d2";
}

/**
 * Default color bins when no variable is selected
 */
export const DEFAULT_COLOR_BINS: ColorBin[] = [
  { min: 0, max: 100, color: "#e3f2fd", label: "0-100" },
  { min: 100, max: 500, color: "#90caf9", label: "100-500" },
  { min: 500, max: 2000, color: "#42a5f5", label: "500-2,000" },
  { min: 2000, max: 9999, color: "#1976d2", label: "2,000-9,999" },
  { min: 9999, max: Infinity, color: "#0d47a1", label: "9,999+" },
];

/**
 * Default fill color for features without data
 */
export const DEFAULT_FILL_COLOR = "#d1d5db";

/**
 * Map feature style configuration
 */
export interface FeatureStyleConfig {
  isHovered: boolean;
  fillColor: string;
}

/**
 * Get base style for a map feature
 */
export function getBaseFeatureStyle(config: FeatureStyleConfig): PathOptions {
  const { isHovered, fillColor } = config;

  return {
    color: isHovered ? "#6b7280" : "#9ca3af",
    weight: isHovered ? 3 : 1.5,
    opacity: 1,
    fillColor,
    fillOpacity: isHovered ? 0.85 : 0.6,
  };
}

/**
 * Get hover style for a map feature
 */
export function getHoverStyle(fillColor: string): PathOptions {
  return {
    weight: 3,
    color: "#6b7280",
    fillColor,
    fillOpacity: 0.85,
  };
}

/**
 * Get default (non-hover) style for a map feature
 */
export function getDefaultStyle(fillColor: string): PathOptions {
  return {
    color: "#9ca3af",
    weight: 1.5,
    opacity: 1,
    fillColor,
    fillOpacity: 0.6,
  };
}

/**
 * Threat badge color classes by threat type slug
 */
export const THREAT_BADGE_CLASSES: Record<string, string> = {
  widespread_winds: "threat-badge-blue",
  damaging_thunderstorm_winds: "threat-badge-blue",
  snow: "threat-badge-gray",
  freezing_rain: "threat-badge-gray",
  extreme_cold: "threat-badge-gray",
  thunderstorms: "threat-badge-blue",
  flooding: "threat-badge-blue",
  tornadoes: "threat-badge-red",
};

/**
 * Get threat badge class for a given threat slug
 */
export function getThreatBadgeClass(threatSlug: string): string {
  return THREAT_BADGE_CLASSES[threatSlug] || "threat-badge-default";
}
